import React, { useEffect, useState, useContext } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import moment from "moment";

import MUIGrid from "@mui/material/Grid2";
import MUITypography from "@mui/material/Typography";
import MUITextField from "@mui/material/TextField";
import { DatePicker as MUIDatePicker } from "@mui/x-date-pickers/DatePicker";
import MUIButton from "@mui/material/Button";
import MUIWheelchairPickupIcon from "@mui/icons-material/WheelchairPickup";

import EmptyState from "../../EmptyState";
import Card from "../../Card";
import User from "../../../Utility/Crud/User";
import Security from "../../../Utility/Security";
import UploadedFile from "../../../Utility/Crud/UploadedFile";

const title = "ATP Assessment";
const icon = MUIWheelchairPickupIcon;

const className = "CardChartAtpAssessment";

export default function CardChartAtpAssessment(props) {
  const chartContext = useContext(ChartContext);
  const mainContext = useContext(MainContext);

  const [editMode, setEditMode] = useState(props.startInEditMode ?? false);
  const [cardChart, setCardChart] = useState(null);
  const [pendingSave, setPendingSave] = useState(false);

  const [mostRecentATPReport, setMostRecentATPReport] = useState(null);
  const order = chartContext.chart.orderActivity[chartContext.orderId];

  const atpAssessment =
    Object.values(chartContext.chart.atpAssessment).find((atpAssessment) => {
      return atpAssessment.orderActivityId === chartContext.orderId;
    }) || [];

  const originalAtpAssessment =
    Object.values(chartContext.originalChart.atpAssessment).find(
      (atpAssessment) => {
        return atpAssessment.orderActivityId === chartContext.orderId;
      },
    ) || [];

  const measurementsInSitting =
    Object.values(chartContext.chart.measurementInSitting).find(
      (measurement) => {
        return measurement.orderActivityId === chartContext.orderId;
      },
    ) || [];

  const atpAssessmentId = atpAssessment?.id ?? chartContext.getNegativeId();

  useEffect(() => {
    chartContext.resetChart();
    setCardChart(null);
  }, [chartContext.pageMode]);

  useEffect(() => {
    if (editMode && atpAssessment.id) {
      chartContext.updateValidationRules({
        key: className,
        rules: [
          {
            target: "atpAssessment",
            id: atpAssessment.id,
            field: "evaluationDate",
            rule: {
              required: true,
            },
          },
        ],
      });
    } else {
      chartContext.updateValidationRules({ key: className, rules: [] });
    }
  }, [editMode, atpAssessment.id]);

  useEffect(() => {
    if (pendingSave) {
      chartContext.handleSaveChart();
      setPendingSave(false);
    }
  }, [pendingSave]);

  useEffect(() => {
    if (!atpAssessment.id && editMode) {
      const atpAssessments = {
        [atpAssessmentId]: {
          id: atpAssessmentId,
          orderActivityId: chartContext.orderId,
          patientId: chartContext.patientId,
          measurementInSittingId:
            measurementsInSitting?.id || chartContext.newMeasurementId || null,
          evaluationDate: null,
          createdById: 1,
          deleted: false,
        },
      };
      chartContext.onChange({ atpAssessment: atpAssessments });
    }
  }, [atpAssessment.id, editMode]);

  useEffect(() => {
    setEditMode(props.startInEditMode ?? true);
  }, [chartContext.originalChart]);

  // Sync this card's edit mode with ChartContext for ChartNavigationBar display
  useEffect(() => {
    chartContext.setCardEditMode?.(className, editMode);
    return () => chartContext.setCardEditMode?.(className, false);
  }, [editMode, chartContext.setCardEditMode]);

  /**
   * Gets the uploaded files for the ATP assessment.
   */
  useEffect(() => {
    const uploadedFileIds = Object.values(chartContext.chart.activityFile)
      .filter((activityFile) => activityFile.activityId === order?.id)
      .map((activityFile) => activityFile.fileId);

    // Get the most recent ATP report file
    const mostRecentATPReport =
      uploadedFileIds
        .map((fileId) => {
          const uploadedFile = chartContext.chart.uploadedFile[fileId];
          if (!uploadedFile) {
            return null;
          }

          if (
            UploadedFile.getActivityFile(uploadedFile, chartContext.chart)
              ?.deleted === true
          ) {
            return null;
          }

          return uploadedFile;
        })
        .filter(Boolean)
        .filter((file) =>
          file.filename.toLowerCase().includes("assistive-technology-report"),
        )
        .sort((a, b) => {
          const dateA = a.uploadedAt ? new Date(a.uploadedAt) : new Date(0);
          const dateB = b.uploadedAt ? new Date(b.uploadedAt) : new Date(0);
          return dateB - dateA;
        })[0] || null;

    setMostRecentATPReport(mostRecentATPReport);
  }, [chartContext.chart.uploadedFile, chartContext.originalChart]);

  const renderContent = () => {
    return Object.values(atpAssessment).length > 0 ? (
      <MUIGrid container>
        {editMode ? (
          <>
            <MUIGrid
              sx={{ mb: 1 }}
              size={{
                xs: 12,
                md: 6,
                lg: 3,
              }}
            >
              <MUIDatePicker
                label="Evaluation Date"
                defaultValue={
                  atpAssessment && atpAssessment.evaluationDate
                    ? moment(atpAssessment.evaluationDate)
                    : null
                }
                sx={{ mb: 1 }}
                slotProps={{
                  field: {
                    clearable: true,
                  },
                  textField: {
                    fullWidth: true,
                    size: "small",
                    variant: "standard",
                    required: true,
                    error: chartContext.validationIssues?.[className]
                      ?.atpAssessment?.[atpAssessmentId]?.evaluationDate
                      ? true
                      : false,
                    helperText:
                      chartContext.validationIssues?.[className]
                        ?.atpAssessment?.[atpAssessmentId]?.evaluationDate ||
                      "",
                    InputLabelProps: {
                      shrink: true,
                    },
                  },
                }}
                onChange={(value) => {
                  chartContext.onChange({
                    atpAssessment: {
                      [atpAssessmentId]: {
                        evaluationDate: value
                          ? moment(value).format("M/D/YYYY")
                          : null,
                      },
                    },
                  });
                  setCardChart({
                    ...cardChart,
                    evaluationDate: value
                      ? moment(value).format("M/D/YYYY")
                      : null,
                  });
                }}
                views={["year", "month", "day"]}
              />
            </MUIGrid>
            <MUIGrid size={12}>
              <MUITextField
                multiline
                rows={4}
                fullWidth={true}
                value={atpAssessment["narrative"] || ""}
                onBlur={(e) =>
                  chartContext.onChange({
                    atpAssessment: {
                      [atpAssessmentId]: {
                        narrative: e.target.value.trim() || null,
                      },
                    },
                  })
                }
                onChange={(e) => {
                  chartContext.onChange({
                    atpAssessment: {
                      [atpAssessmentId]: { narrative: e.target.value },
                    },
                  });
                  setCardChart({ ...cardChart, narrative: e.target.value });
                }}
                label={"Narrative"}
                variant="standard"
                sx={{ mb: 2 }}
                slotProps={{
                  htmlInput: { style: { resize: "vertical" } },
                  inputLabel: { shrink: true },
                }}
              />
            </MUIGrid>
          </>
        ) : (
          <MUIGrid container direction="row" spacing={2}>
            <MUIGrid size="auto">
              <MUITypography variant="subtitle2">Evaluation Date</MUITypography>
              <MUITypography variant="body2">
                {atpAssessment && atpAssessment.evaluationDate
                  ? moment(atpAssessment.evaluationDate).format("M/D/YYYY")
                  : "None"}
              </MUITypography>
            </MUIGrid>
            <MUIGrid size="auto">
              <MUITypography variant="subtitle2">Created By</MUITypography>
              <MUITypography variant="body2">
                {User.getDisplayName(
                  mainContext.usersIndexed[atpAssessment["createdById"]],
                ) || "None"}
              </MUITypography>
            </MUIGrid>
          </MUIGrid>
        )}
      </MUIGrid>
    ) : (
      <EmptyState line1="Nothing to show" line2="Better luck next time!" />
    );
  };

  /**
   * Returns the action buttons for the ATP assessment card. When inline editing is enabled
   * via configuration, a Cancel button (which reverts unsaved changes) is shown while in edit
   * mode and an Edit button (which opens edit mode) is shown otherwise.
   * @returns {Array} Array of action button components
   */
  const getActions = () => {
    const handleCancel = () => {
      const updatedAtpAssessment = { ...atpAssessment };
      const assessmentFields = cardChart ? Object.keys(cardChart) : [];

      for (const field of assessmentFields) {
        if (atpAssessment[field] !== originalAtpAssessment[field]) {
          updatedAtpAssessment[field] = originalAtpAssessment[field];
        }
      }
      chartContext.onChange({
        atpAssessment: { [atpAssessment.id]: { ...updatedAtpAssessment } },
      });
      chartContext.setCardEditMode?.(className, false);
      setEditMode(false);

      const clearedIssues = { ...chartContext.validationIssues };
      delete clearedIssues[className];
      chartContext.updateValidationIssues(clearedIssues);
    };

    const actions = [];

    if (!props.enableNormalEditMode) {
      return actions;
    }

    actions.push(
      editMode ? (
        <MUIButton
          color="error"
          disabled={
            chartContext.isUpdating ||
            !Security.hasPermission("module.manageAtpAssessment")
          }
          onClick={handleCancel}
          size="small"
        >
          Cancel
        </MUIButton>
      ) : (
        <MUIButton
          disabled={
            chartContext.isUpdating ||
            !Security.hasPermission("module.manageAtpAssessment")
          }
          onClick={() => setEditMode(true)}
          size="small"
        >
          Edit
        </MUIButton>
      ),
    );

    return actions;
  };

  return (
    <Card
      renderContent={renderContent}
      getActions={getActions}
      getContainerType={() => "inline-block"}
      getTitle={() => title}
      getIcon={() => icon}
    />
  );
}

CardChartAtpAssessment.meta = {
  className: className,
  title: title,
  icon: icon,
};
